/**
 * Proposal Timber Apply
 * ---------------------
 * Pure function that applies a `ProposalTimberDiff` to an existing
 * `ProposalBuilderData` according to per-structure user selections from the
 * Update Timber List modal.
 *
 * Apply semantics
 * - Modified line: pull pieceName / quantity / dimensions / boardFeet from CSV;
 *   preserve manual species/grade/moisture; preserve unitCost when dimensions
 *   are unchanged, otherwise zero unitCost so the existing DB-pricing pathway
 *   refills it. Recompute `total = unitCost * quantity`.
 * - Added line: instantiate via the same defaults the existing import flow
 *   uses, inheriting structure-level default species/grade if present.
 * - Removed line: dropped from `materials.timberList`.
 * - Added structure: created via `createDefaultNodeSet` populated like
 *   `appendNodeSetsFromCsvResult`.
 * - Deleted structure: filtered out of `nodeSets`.
 * - Alternate species lists are intentionally left untouched in v1.
 */

import type { ProposalNodeSet, TimberListItem } from '../types';
import { createDefaultNodeSet } from '../types';
import {
  DEFAULT_PROPOSAL_TIMBER_SPECIES,
  DEFAULT_PROPOSAL_TIMBER_GRADE,
  convertToTimberListItems,
  type ParsedTimberItem,
} from './proposalCSVParser';
import { parseTimberMaterialColumn } from './timberMaterialParser';
import {
  type ProposalTimberDiff,
  type StructureDiff,
  type LineChange,
  type ProposalLikeWithNodeSets,
  normalizeDimensionsForMatch,
} from './proposalTimberDiff';
import {
  isProductionBoardFeetOverridden,
  resolveProductionBoardFeet,
  syncProductionBoardFeetFromMaterials,
} from './productionBoardFeet';

export interface TimberDiffSelections {
  /** Existing structure ids whose `updated` diff should be applied. */
  applyStructureIds: Set<string>;
  /** CSV-only group names (kind === 'added') that should be inserted as new structures. */
  addNewGroupNames: Set<string>;
  /** Existing structure ids (kind === 'removedFromCsv') that should be deleted from the proposal. */
  deleteStructureIds: Set<string>;
  /**
   * Reorder every matched structure (kind === 'updated' OR 'unchanged') so its
   * `materials.timberList` walks in the CSV's row order — preserving each
   * line's content (species, grade, unitCost, ids…) and only changing
   * positions. Useful for verifying a proposal against the CSV row-by-row when
   * no content actually changed but the existing list drifted out of order.
   *
   * Structures being updated already emerge in CSV order from `applyLineChanges`,
   * so this flag only does extra work for the *non-updated* matched structures.
   */
  reorderToCsvOrder?: boolean;
}

export interface ApplyResult<TState extends ProposalLikeWithNodeSets = ProposalLikeWithNodeSets> {
  state: TState;
  /**
   * Existing structure ids whose lines had real *content* changes (modified /
   * added / removed lines, or a brand-new structure). Caller uses this to
   * trigger DB-price re-sync — reorder-only structures are intentionally
   * excluded so a pure reorder doesn't clobber hand-set unit costs.
   */
  touchedNodeSetIds: string[];
  /**
   * Existing structure ids that were ONLY reordered (no content delta). Useful
   * for status messaging; the caller should NOT pass these to the price
   * resync.
   */
  reorderedOnlyNodeSetIds: string[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function inferStructureDefaults(list: TimberListItem[]): { species: string; grade: string } {
  const firstWithSpecies = list.find(t => (t.species || '').trim().length > 0);
  const firstWithGrade = list.find(t => (t.grade || '').trim().length > 0);
  return {
    species: firstWithSpecies?.species || DEFAULT_PROPOSAL_TIMBER_SPECIES,
    grade: firstWithGrade?.grade || DEFAULT_PROPOSAL_TIMBER_GRADE,
  };
}

export function recomputeStructureTotals(ns: ProposalNodeSet): ProposalNodeSet {
  const list = ns.materials.timberList || [];
  const timberTotal = list.reduce((sum, it) => sum + (Number(it.total) || 0), 0);
  const totalBoardFeet = list.reduce(
    (sum, it) => sum + (Number(it.boardFeet) || 0) * (Number(it.quantity) || 0),
    0,
  );
  const newMaterials = {
    ...ns.materials,
    timberList: list,
    timberTotal: round2(timberTotal),
    totalBoardFeet: round2(totalBoardFeet),
    total: round2(
      timberTotal +
      (Number(ns.materials.pegsCost) || 0) +
      (Number(ns.materials.postBasesTotal) || 0) +
      (Number(ns.materials.fastenerPackageCost) || 0) +
      (Number(ns.materials.deckingTotal) || 0),
    ),
  };
  // Preserve the per-structure $/bdft rate so it survives CSV updates.
  // Source of truth is `production.ratePerBdft` when present (no float
  // drift); fall back to the implied `total/boardFeet` for legacy
  // structures saved before that field existed.
  const oldBf = Number(ns.production.boardFeet) || 0;
  const oldTotal = Number(ns.production.total) || 0;
  const newBfMaterials = round2(totalBoardFeet);
  const productionBase = syncProductionBoardFeetFromMaterials(ns.production, newBfMaterials);
  const useBf = round2(resolveProductionBoardFeet(productionBase, newBfMaterials));
  const persistedRate = Number(ns.production.ratePerBdft);
  const rate = Number.isFinite(persistedRate) && persistedRate > 0
    ? persistedRate
    : oldBf > 0 && oldTotal > 0
      ? oldTotal / oldBf
      : 0;
  const newProduction = {
    ...productionBase,
    boardFeet: useBf,
    boardFeetOverridden: isProductionBoardFeetOverridden(productionBase, newBfMaterials),
    total: round2(rate * useBf),
  };
  return { ...ns, materials: newMaterials, production: newProduction };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Fresh id for a new line within an existing structure on apply. */
function freshLineId(prefix: string, salt: number): string {
  return `${prefix}-${Date.now()}-${salt}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Build a new TimberListItem from a CSV row using parsed material or structure defaults. */
function buildNewLine(
  csv: ParsedTimberItem,
  defaults: { species: string; grade: string },
  salt: number,
): TimberListItem {
  const parsed = parseTimberMaterialColumn(csv.material, {
    speciesFallback: defaults.species,
    gradeFallback: defaults.grade,
  });
  return {
    id: freshLineId('timber', salt),
    name: csv.pieceName,
    species: parsed.species,
    grade: parsed.grade,
    dimensions: csv.normalizedDimensions,
    moistureContent: '' as const,
    quantity: csv.quantity,
    boardFeet: csv.boardFeet,
    unitCost: 0,
    total: 0,
    sourceLineNumber: csv.lineNumber,
    sourceProductionNumber: csv.productionNumber,
  };
}

/**
 * Reorder an existing line list so it walks in the CSV's row order, preserving
 * each line's content verbatim (no field changes, no ids reissued).
 *
 * Implementation note: `lineChanges` are already CSV-ordered with proposal-
 * only `removed` rows appended at the end (see `diffStructureLines`). For each
 * lineChange we push the original `before` item if present; CSV-only `added`
 * rows are skipped because they have no existing content to carry over.
 * Anything still unaccounted for at the end (defensive) is appended in its
 * original list order.
 */
function reorderLinesToCsvOrder(
  existing: TimberListItem[],
  lineChanges: LineChange[],
): TimberListItem[] {
  const out: TimberListItem[] = [];
  const consumedIds = new Set<string>();
  for (const lc of lineChanges) {
    if (lc.before) {
      consumedIds.add(lc.before.id);
      out.push(lc.before);
    }
    // 'added' rows (CSV-only) are intentionally skipped: this is a reorder,
    // not a content import.
  }
  for (const e of existing) {
    if (!consumedIds.has(e.id)) out.push(e);
  }
  return out;
}

/** Cheap reference-equality order check; lines we'd push are the same objects. */
function sameOrder(a: TimberListItem[], b: TimberListItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}

/** Apply a single structure's selected line changes to its existing list. */
function applyLineChanges(existing: TimberListItem[], lineChanges: LineChange[]): TimberListItem[] {
  const defaults = inferStructureDefaults(existing);

  // Track which existing items have been consumed (matched/removed) so the
  // surviving "unchanged" lines can pass through verbatim.
  const consumedIds = new Set<string>();
  const newItems: TimberListItem[] = [];
  let saltCounter = 0;

  for (const lc of lineChanges) {
    if (lc.kind === 'unchanged') {
      if (lc.before) {
        consumedIds.add(lc.before.id);
        newItems.push(lc.before);
      }
      continue;
    }
    if (lc.kind === 'removed') {
      if (lc.before) consumedIds.add(lc.before.id);
      continue;
    }
    if (lc.kind === 'modified' && lc.before && lc.after) {
      consumedIds.add(lc.before.id);
      const before = lc.before;
      const dimsChanged =
        normalizeDimensionsForMatch(before.dimensions) !==
        normalizeDimensionsForMatch(lc.after.dimensions);
      const newQty = lc.after.quantity;
      const preservedUnitCost = dimsChanged ? 0 : Number(before.unitCost) || 0;
      const updated: TimberListItem = {
        ...before,
        name: lc.after.pieceName,
        dimensions: lc.after.dimensions,
        boardFeet: lc.after.boardFeet,
        quantity: newQty,
        unitCost: preservedUnitCost,
        total: round2(preservedUnitCost * newQty),
        sourceLineNumber: before.sourceLineNumber ?? lc.after.lineNumber,
        sourceProductionNumber: before.sourceProductionNumber ?? lc.after.productionNumber,
      };
      newItems.push(updated);
      continue;
    }
    if (lc.kind === 'added' && lc.after) {
      saltCounter += 1;
      const csvItem: ParsedTimberItem = {
        pieceName: lc.after.pieceName,
        group: '',
        material: '',
        quantity: lc.after.quantity,
        height: 0,
        width: 0,
        orderLength: 0,
        normalizedDimensions: lc.after.dimensions,
        boardFeet: lc.after.boardFeet,
        lineNumber: lc.after.lineNumber ?? saltCounter,
        productionNumber: lc.after.productionNumber,
      };
      newItems.push(buildNewLine(csvItem, defaults, saltCounter));
      continue;
    }
  }

  // Any existing line not referenced by the diff (defensive — shouldn't happen
  // in practice since diff covers every existing item) is preserved at the end.
  for (const e of existing) {
    if (!consumedIds.has(e.id)) newItems.push(e);
  }

  return newItems;
}

/** Build a fresh ProposalNodeSet for a CSV-only group, mirroring the existing append flow. */
function buildNodeSetFromCsv(groupName: string, csvItems: ParsedTimberItem[], salt: number): ProposalNodeSet {
  const timberLines = convertToTimberListItems(csvItems);
  const ns = createDefaultNodeSet(groupName);
  ns.id = `nodeset-${Date.now()}-${salt}-${Math.random().toString(36).slice(2, 7)}`;
  return recomputeStructureTotals({
    ...ns,
    materials: {
      ...ns.materials,
      timberList: timberLines,
    },
  });
}

// ─── Main apply ────────────────────────────────────────────────────────────────

export function applyProposalTimberDiff<TState extends ProposalLikeWithNodeSets>(
  state: TState,
  diff: ProposalTimberDiff,
  selections: TimberDiffSelections,
): ApplyResult<TState> {
  // Quick lookups
  const updateById = new Map<string, StructureDiff>();
  diff.structureDiffs.forEach(sd => {
    if (sd.kind === 'updated' && sd.existingNodeSetId) {
      updateById.set(sd.existingNodeSetId, sd);
    }
  });

  // Structures that match a CSV group AND have no content changes — used by
  // the reorder-only pass below.
  const matchedById = new Map<string, StructureDiff>();
  diff.structureDiffs.forEach(sd => {
    if ((sd.kind === 'updated' || sd.kind === 'unchanged') && sd.existingNodeSetId) {
      matchedById.set(sd.existingNodeSetId, sd);
    }
  });

  const addByGroup = new Map<string, StructureDiff>();
  diff.structureDiffs.forEach(sd => {
    if (sd.kind === 'added' && sd.csvGroupName) {
      addByGroup.set(sd.csvGroupName, sd);
    }
  });

  const touched: string[] = [];
  const reorderedOnly: string[] = [];

  // Walk existing structures, mutating selected updates and dropping selected deletions.
  let nodeSets: ProposalNodeSet[] = [];
  let saltSeed = 0;
  for (const ns of state.nodeSets) {
    if (selections.deleteStructureIds.has(ns.id)) {
      // Dropped entirely.
      continue;
    }
    const update = updateById.get(ns.id);
    if (update && selections.applyStructureIds.has(ns.id)) {
      // Updates already produce CSV-ordered output via applyLineChanges,
      // so the reorder flag is implicitly satisfied for this branch.
      const newList = applyLineChanges(ns.materials.timberList || [], update.lineChanges);
      const next = recomputeStructureTotals({
        ...ns,
        materials: { ...ns.materials, timberList: newList },
      });
      nodeSets.push(next);
      touched.push(ns.id);
      continue;
    }
    // Reorder-only pass: structure has a CSV match (updated kind that wasn't
    // ticked, or unchanged kind) and the user asked to align row order. We
    // skip it when the order is already CSV-aligned to avoid spurious diffs.
    // Tracked separately from `touched` so the caller can keep it out of the
    // DB price resync (reorder must not overwrite hand-set unit costs).
    if (selections.reorderToCsvOrder) {
      const matched = matchedById.get(ns.id);
      if (matched) {
        const reordered = reorderLinesToCsvOrder(ns.materials.timberList || [], matched.lineChanges);
        if (!sameOrder(reordered, ns.materials.timberList || [])) {
          const next = recomputeStructureTotals({
            ...ns,
            materials: { ...ns.materials, timberList: reordered },
          });
          nodeSets.push(next);
          reorderedOnly.push(ns.id);
          continue;
        }
      }
    }
    nodeSets.push(ns);
  }

  // Append selected new structures. Use Array.from to avoid relying on Set
  // iteration (target/downlevelIteration not enabled in this project).
  Array.from(selections.addNewGroupNames).forEach(groupName => {
    const sd = addByGroup.get(groupName);
    if (!sd || !sd.csvItems) return;
    saltSeed += 1;
    const ns = buildNodeSetFromCsv(groupName, sd.csvItems, saltSeed);
    nodeSets.push(ns);
    touched.push(ns.id);
  });

  return {
    state: { ...state, nodeSets },
    touchedNodeSetIds: touched,
    reorderedOnlyNodeSetIds: reorderedOnly,
  };
}
